import '@modules/product-add-cta/product-add-cta.js';
import '@modules/quantity-input/quantity-input.js';
import ProductMultiAdd from '@modules/product-multi-add/product-multi-add.js';
import { getDelta } from '@/js/countdown-timer-actions.js';
import { $bundleItemsCount } from '@/js/bundle-store';

const PURCHASE_MODE_RADIO_SELECTOR = 'input[name="product-form"]';
const INDIVIDUAL_ADD_CTA_SELECTOR =
  'product-add-cta[data-product-form-individual-cta]';

/**
 * Bundleable product form with bulk vs individual purchase mode radios.
 * @extends HTMLElement
 */
class ProductFormElement extends HTMLElement {
  connectedCallback() {
    this.setup();
  }

  /**
   * @returns {void}
   */
  setup() {
    this.form = this.querySelector('form.product-form');

    if (!(this.form instanceof HTMLFormElement)) {
      return;
    }

    if (
      this.isComingSoon() &&
      !this.form.querySelector(PURCHASE_MODE_RADIO_SELECTOR)
    ) {
      this.getQuantityInput()?.disable();
      return;
    }

    if (
      !Object.hasOwn(this.form.dataset, 'bundleable') ||
      !this.form.querySelector(PURCHASE_MODE_RADIO_SELECTOR)
    ) {
      return;
    }

    this.bindEventListeners();
    this.initPurchaseMode();
  }

  /**
   * Sets default purchase mode and syncs disabled state for bulk vs individual paths.
   * Coming-soon products leave both radios and pack options unselected.
   * @returns {void}
   */
  initPurchaseMode() {
    if (this.isComingSoon()) {
      this.clearPurchaseModeSelection();
      this.syncPurchaseModeDisabledState();
      return;
    }

    const radio = this.form.querySelector(
      `${PURCHASE_MODE_RADIO_SELECTOR}[value="${this.getDefaultPurchaseMode()}"]`
    );
    if (radio instanceof HTMLInputElement) {
      radio.checked = true;
    }
    this.syncPurchaseModeDisabledState();
  }

  /**
   * @returns {boolean}
   */
  isComingSoon() {
    return Object.hasOwn(this.form.dataset, 'comingSoon');
  }

  /**
   * Clears purchase-mode and pack radios so none appear selected.
   * @returns {void}
   */
  clearPurchaseModeSelection() {
    for (const radio of this.form.querySelectorAll(
      PURCHASE_MODE_RADIO_SELECTOR
    )) {
      if (radio instanceof HTMLInputElement) {
        radio.checked = false;
      }
    }

    for (const packRadio of this.form.querySelectorAll('[data-pack-radio]')) {
      if (packRadio instanceof HTMLInputElement) {
        packRadio.checked = false;
      }
    }
  }

  /**
   * Syncs disabled state for the active purchase mode.
   * @returns {void}
   */
  syncPurchaseModeDisabledState() {
    const mode = this.getCheckedPurchaseMode();
    if (!mode) {
      ProductMultiAdd.disableInForm(this.form);
      this.disableIndividualPath();
      return;
    }

    if (mode === 'individual') {
      ProductMultiAdd.disableInForm(this.form);
      if (this.canEnableIndividualPath()) {
        this.enableIndividualPath();
      } else {
        this.disableIndividualPath();
      }
      return;
    }

    if (this.canEnableBulkPath()) {
      ProductMultiAdd.enableInForm(this.form);
    } else {
      ProductMultiAdd.disableInForm(this.form);
    }
    this.disableIndividualPath();
  }

  /**
   * @returns {string | undefined}
   */
  getCheckedPurchaseMode() {
    const checked = this.form.querySelector(
      `${PURCHASE_MODE_RADIO_SELECTOR}:checked`
    );
    return checked instanceof HTMLInputElement ? checked.value : undefined;
  }

  /**
   * @returns {'bulk' | 'individual'}
   */
  getDefaultPurchaseMode() {
    return $bundleItemsCount.get() > 0 ? 'individual' : 'bulk';
  }

  /**
   * @returns {Element | null}
   */
  getIndividualAddCta() {
    return this.form.querySelector(INDIVIDUAL_ADD_CTA_SELECTOR);
  }

  /**
   * @returns {Element | null}
   */
  getQuantityInput() {
    return this.form.querySelector('quantity-input');
  }

  /**
   * @returns {HTMLButtonElement | undefined}
   */
  getIndividualAddButton() {
    return this.getIndividualAddCta()?.button;
  }

  /**
   * @param {HTMLButtonElement | null | undefined} button
   * @returns {boolean}
   */
  isLaunchDateBlocking(button) {
    const launchDate = button?.dataset.launchDate;
    if (!launchDate) {
      return false;
    }
    const delta = getDelta(launchDate);
    return delta > 0 && !Number.isNaN(Number(delta));
  }

  /**
   * @param {HTMLButtonElement | null | undefined} button
   * @returns {boolean}
   */
  isSoldOut(button) {
    const maxInventoryQuantity = button?.getAttribute('max-inventory-quantity');
    return (
      !!maxInventoryQuantity && Number.parseInt(maxInventoryQuantity, 10) === 0
    );
  }

  /**
   * @param {HTMLButtonElement | null | undefined} button
   * @returns {boolean}
   */
  isKyotoBlocking(button) {
    if (!button?.hasAttribute('kyoto-only-product')) {
      return false;
    }
    const checkbox = this.form.querySelector(
      '[data-kyoto-only-product-checkbox]'
    );
    return !checkbox?.checked;
  }

  /**
   * @returns {boolean}
   */
  canEnableIndividualPath() {
    const button = this.getIndividualAddButton();
    return (
      !!button &&
      !Object.hasOwn(button.dataset, 'accessDenied') &&
      Object.hasOwn(button.dataset, 'addToCart') &&
      !this.isSoldOut(button) &&
      !this.isLaunchDateBlocking(button) &&
      !this.isKyotoBlocking(button)
    );
  }

  /**
   * @returns {boolean}
   */
  canEnableBulkPath() {
    const button = this.getIndividualAddButton();
    return !this.isLaunchDateBlocking(button) && !this.isKyotoBlocking(button);
  }

  /**
   * @returns {void}
   */
  disableIndividualPath() {
    this.getIndividualAddCta()?.disableButton();
    this.getQuantityInput()?.disable();
  }

  /**
   * @returns {void}
   */
  enableIndividualPath() {
    if (!this.canEnableIndividualPath()) {
      this.disableIndividualPath();
      return;
    }

    this.getIndividualAddCta()?.enableButton();

    const quantityInput = this.getQuantityInput();
    if (quantityInput) {
      quantityInput.enable();
      quantityInput.disableAndEnableButtons();
    }
  }

  /**
   * @returns {void}
   */
  bindEventListeners() {
    this.addEventListener('change', event => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement &&
        target.name === 'product-form' &&
        target.type === 'radio' &&
        this.form.contains(target)
      ) {
        this.syncPurchaseModeDisabledState();
      }
    });
    this.addEventListener('pxl:productCheckboxChange', () => {
      this.syncPurchaseModeDisabledState();
    });
  }
}

customElements.get('product-form-element') ||
  customElements.define('product-form-element', ProductFormElement);

export default ProductFormElement;
